import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { queryKeys } from '~/api/queryKeys';
import type { FeedAuditJobDetail } from '~/features/feed-audit/types/feed-audit.types';
import { useArchiveFeedback } from '~/features/list-archive/hooks/use-archive-feedback';

import { setEnrichmentJobArchived } from '../services/catalog-operations.service';
import type { BatchHeader, BatchRecord } from '../types/catalog-operations.types';

interface SetEnrichmentJobArchivedArgs {
  jobId: string;
  archived: boolean;
  /** Set on the single automatic retry after `ARCHIVE_STATE_CHANGED`. */
  retried?: boolean;
}

/** A refusal the UI must act on: the run is provably executing
 *  (`JOB_STILL_LIVE` with `liveness = OPEN`). `workflowId` deep-links Stop. */
export interface EnrichmentJobArchiveRefusal {
  jobId: string;
  workflowId: string | null;
}

/**
 * Archive / unarchive toggle for enrichment jobs (Catalog Operations + Feed
 * Audit share the row). On success the response row's attribution stamps
 * (`archivedAt`, `archivedBy`, `lastActionBy/At/Type`) are patched into the cached
 * detail header and Feed Audit detail — so an open detail page flips
 * immediately — and the whole catalog-operations subtree is invalidated (lists
 * are keyed per filter permutation, so a refetch beats patching every slot).
 *
 * Refusals: `ARCHIVE_STATE_CHANGED` re-reads and retries once, silently; a
 * provably-live run (`JOB_STILL_LIVE` + OPEN) is exposed as `refusal` for the
 * owning control to render a Stop / Reconcile dialog; everything else toasts.
 */
export function useSetEnrichmentJobArchived() {
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useArchiveFeedback();
  const [refusal, setRefusal] = useState<EnrichmentJobArchiveRefusal | null>(null);

  const mutation = useMutation<BatchRecord, unknown, SetEnrichmentJobArchivedArgs>({
    mutationFn: ({ jobId, archived }) => setEnrichmentJobArchived(jobId, archived),
    onSuccess: (row, { jobId, archived }) => {
      const stamps = {
        archivedAt: row.archivedAt ?? null,
        archivedBy: row.archivedBy ?? null,
        ...(row.lastActionBy != null ? { lastActionBy: row.lastActionBy } : {}),
        ...(row.lastActionAt != null ? { lastActionAt: row.lastActionAt } : {}),
        ...(row.lastActionType != null ? { lastActionType: row.lastActionType } : {}),
      };
      queryClient.setQueryData<BatchHeader>(
        queryKeys.catalogOperations.batchHeader(jobId),
        (prev) => (prev ? { ...prev, ...stamps } : prev),
      );
      queryClient.setQueryData<FeedAuditJobDetail>(queryKeys.feedAudit.detail(jobId), (prev) =>
        prev ? { ...prev, archivedAt: stamps.archivedAt, archivedBy: stamps.archivedBy } : prev,
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalogOperations.all });
      setRefusal(null);
      notifySuccess(
        archived,
        archived
          ? () => {
              mutation.mutate({ jobId, archived: false });
            }
          : undefined,
      );
    },
    onError: (err, { jobId, archived, retried }) => {
      const refused = notifyError(err);
      if (refused.kind === 'state-changed' && !retried) {
        // Someone moved the flag between our read and our write. Not a user
        // error: refresh what we show and retry exactly once.
        void queryClient.invalidateQueries({ queryKey: queryKeys.catalogOperations.all });
        mutation.mutate({ jobId, archived, retried: true });
        return;
      }
      if (refused.kind === 'still-live' && refused.liveness === 'OPEN' && archived) {
        setRefusal({ jobId, workflowId: refused.workflowId });
        return;
      }
      if (refused.kind === 'not-found') {
        // The row vanished underneath us — drop it from every list.
        void queryClient.invalidateQueries({ queryKey: queryKeys.catalogOperations.all });
      }
    },
  });

  return {
    /** Archives the job. Resolves on 200 and rejects on failure (already
     *  toasted, or handed to `refusal`) so a confirm popover can close itself. */
    archive: (jobId: string) => mutation.mutateAsync({ jobId, archived: true }),
    /** Unarchives the job — fire-and-forget; never refused by the server. */
    unarchive: (jobId: string) => {
      mutation.mutate({ jobId, archived: false });
    },
    /** Id of the job whose toggle is in flight, for a per-row spinner. */
    pendingJobId: mutation.isPending ? mutation.variables.jobId : null,
    /** Set when the last archive was refused because the run is executing. */
    refusal,
    dismissRefusal: () => {
      setRefusal(null);
    },
  };
}
